import {
  createContext,
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { RejectedInvoice } from '../types'
import { useSelector } from 'react-redux'
import {
  getChangedLines,
  getFetchedLines,
  getLoadingMore,
  getTotal
} from '../../../../redux/store/rejectedInvoices/getters'
import { SORT_OPTIONS } from '../constants'
import { setLoading } from '../../../../redux/store/common/slice'
import { sendEditedLines } from '../api'
import { useDebounce } from '../../../../hooks/hooks'
import { setChangedLines } from '../../../../redux/store/rejectedInvoices/slice'
import { notification } from '../../../../components/Notification'
import useInfiniteData from '../../../../hooks/useInfiniteData'
import { getRejectedInvoicesListActionAsync } from '../../../../redux/store/rejectedInvoices/actions'
import { AxiosResponse, CancelTokenSource } from 'axios'
import { RejectedInvoicesRequestArgs } from '../../../../redux/store/rejectedInvoices/types'
import { useAppDispatch } from '../../../../redux/createStore'

type ContextProps = {
  state: {
    searchValue: string
    selectedSort: string
    rejectedInvoices: RejectedInvoice[]
    firstLoad: boolean
    activeKey: string
    isActive: boolean
    loadingMore: boolean
  }
  actions: {
    setSearchValue: Dispatch<SetStateAction<string>>
    setSelectedSort: Dispatch<SetStateAction<string>>
    onChange: (val: string | string[]) => void
    onSave: VoidFunction
    onCancel: VoidFunction
    handleLoadMore: VoidFunction
  }
}

const RejectedInvoicesListContext = createContext<ContextProps>({
  state: {} as ContextProps['state'],
  actions: {} as ContextProps['actions']
})

const RejectedInvoicesListContextProvider: FC<PropsWithChildren> = ({
  children
}) => {
  const [isActive, _setIsActive] = useState(false)
  const [firstLoad, _setFirstLoad] = useState(true)
  const [selectedSort, setSelectedSort] = useState('')
  const [searchValue, setSearchValue] = useState('')
  const [activeKey, _setActiveKey] = useState('')
  const [rejectedInvoices, setRejectedInvoices] = useState<RejectedInvoice[]>(
    []
  )

  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const tableDataTokenRef = useRef<CancelTokenSource>()
  const dispatch = useAppDispatch()
  const list = useSelector(getFetchedLines)
  const changedLines = useSelector(getChangedLines)
  const total = useSelector(getTotal)
  const loadingMore = useSelector(getLoadingMore)
  const debouncedSearch = useDebounce(searchValue, 500)

  const setupRequestArgs = useCallback((): RejectedInvoicesRequestArgs => {
    const args: RejectedInvoicesRequestArgs = {}
    const selectedSortValue = SORT_OPTIONS.find(
      (i) => i.value === selectedSort
    )?.value
    if (selectedSortValue) {
      args.ordering = selectedSortValue
    }
    if (searchValue) {
      args.search = searchValue
    }
    if (tableDataTokenRef.current) {
      args.cancelToken = tableDataTokenRef
    }
    return args
  }, [searchValue, selectedSort])

  const loadMore = useCallback(
    async (args: object) => {
      try {
        dispatch(
          getRejectedInvoicesListActionAsync({
            ...args,
            ...setupRequestArgs()
          })
        )
      } catch (e) {
        const err = e as AxiosResponse
        console.error(err)
        notification.error({ message: err?.data.details })
      }
    },
    [dispatch, setupRequestArgs]
  )

  useEffect(() => {
    setRejectedInvoices(list)
  }, [list])

  const { limit, offset, handleLoadMore, reset } = useInfiniteData({
    total,
    loadMore,
    limit: 20
  })

  const initRequest = useCallback(async () => {
    try {
      if (offset !== 0) {
        reset()
      }
      dispatch(
        getRejectedInvoicesListActionAsync({
          limit,
          offset: 0,
          ...setupRequestArgs()
        })
      )
      if (firstLoad) {
        _setFirstLoad(false)
      }
    } catch (e) {
      const err = e as AxiosResponse
      console.error(err)
      notification.error({ message: err?.data.details })
    }
  }, [dispatch, firstLoad, limit, offset, reset, setupRequestArgs])

  const onChange = useCallback(
    (uuid: string | string[]) =>
      _setActiveKey(Array.isArray(uuid) ? uuid[0] : uuid),
    []
  )

  const onCancel = useCallback(() => {
    _setIsActive(false)
    dispatch(setChangedLines([]))
  }, [dispatch])

  const onSave = useCallback(async () => {
    try {
      dispatch(setLoading(true))
      const invoicesIds = new Set<string>()
      rejectedInvoices.forEach((inv) => {
        if (
          inv.lines.find((i) => changedLines.find((j) => j.uuid === i.uuid))
        ) {
          invoicesIds.add(inv.lines[0].document_key)
        }
      })
      const invoicesLines = rejectedInvoices
        .filter((i) => invoicesIds.has(i.lines[0].document_key))
        .flatMap((i) => i.lines)
      const linesToSend = invoicesLines.map((line) => {
        const changedLine = changedLines.find((i) => line.uuid === i.uuid)
        if (changedLine) {
          return changedLine
        }
        return line
      })
      await sendEditedLines(linesToSend)
      _setActiveKey('')
      await initRequest()
      onCancel()
    } finally {
      dispatch(setLoading(false))
    }
  }, [dispatch, rejectedInvoices, initRequest, onCancel, changedLines])

  useEffect(() => {
    return () => {
      if (!!tableDataTokenRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        tableDataTokenRef.current.cancel()
      }
      _setIsActive(false)
      _setFirstLoad(true)
    }
  }, [])

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(initRequest, firstLoad ? 0 : 500)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, selectedSort])

  useEffect(() => {
    _setIsActive(!!changedLines.length)
  }, [changedLines])

  useEffect(() => {
    if (firstLoad) onCancel()
  }, [firstLoad, onCancel])

  const context = useMemo(
    () => ({
      state: {
        searchValue,
        selectedSort,
        rejectedInvoices,
        firstLoad,
        activeKey,
        isActive,
        loadingMore
      },
      actions: {
        setSearchValue,
        setSelectedSort,
        handleLoadMore,
        onChange,
        onSave,
        onCancel
      }
    }),
    [
      loadingMore,
      activeKey,
      firstLoad,
      isActive,
      onCancel,
      onChange,
      onSave,
      handleLoadMore,
      rejectedInvoices,
      searchValue,
      selectedSort
    ]
  )

  return (
    <RejectedInvoicesListContext.Provider value={context}>
      {children}
    </RejectedInvoicesListContext.Provider>
  )
}

export const useRejectedInvoicesListContext = () =>
  useContext(RejectedInvoicesListContext)

export default RejectedInvoicesListContextProvider
